import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SERVICES, PHONE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", service: "", message: "" };

const fieldClass =
  "h-13 rounded-xl border-white/10 bg-white/5 px-5 py-4 text-base text-white placeholder:text-zinc-600 focus-visible:border-gold/60 focus-visible:ring-gold/30";

const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, service: form.service || null };
      const { data } = await axios.post(`${API}/enquiries`, payload);
      toast.success("Request received.", {
        description: `Reference ${data.id}. A dedicated specialist will contact you shortly.`,
      });
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("We couldn't submit your request.", {
        description: typeof detail === "string" ? detail : `Please call us on ${PHONE}.`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="enquiry-form" className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Input
          required
          minLength={2}
          value={form.name}
          onChange={update("name")}
          placeholder="Full name"
          data-testid="enquiry-name-input"
          className={fieldClass}
          aria-label="Full name"
        />
        <Input
          required
          minLength={7}
          type="tel"
          value={form.phone}
          onChange={update("phone")}
          placeholder="Phone number"
          data-testid="enquiry-phone-input"
          className={fieldClass}
          aria-label="Phone number"
        />
      </div>
      <Input
        required
        type="email"
        value={form.email}
        onChange={update("email")}
        placeholder="Email address"
        data-testid="enquiry-email-input"
        className={fieldClass}
        aria-label="Email address"
      />
      <select
        value={form.service}
        onChange={update("service")}
        data-testid="enquiry-service-select"
        aria-label="Service required"
        className="h-13 w-full rounded-xl border border-white/10 bg-white/5 px-5 py-4 text-base text-white outline-none transition-colors duration-300 focus:border-gold/60 [&>option]:bg-carbon"
      >
        <option value="">What do you need help with? (optional)</option>
        {SERVICES.map((s) => (
          <option key={s.title} value={s.title}>
            {s.title}
          </option>
        ))}
      </select>
      <Textarea
        required
        minLength={5}
        rows={5}
        value={form.message}
        onChange={update("message")}
        placeholder="Tell us briefly what happened — where, when, and the vehicles involved."
        data-testid="enquiry-message-input"
        className="rounded-xl border-white/10 bg-white/5 px-5 py-4 text-base text-white placeholder:text-zinc-600 focus-visible:border-gold/60 focus-visible:ring-gold/30"
        aria-label="Your message"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="enquiry-submit-button"
        className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-bold uppercase tracking-[0.2em] text-obsidian transition-[background-color,transform,opacity] duration-300 hover:bg-gold-hover hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending
          </>
        ) : (
          <>
            Request Assistance
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </>
        )}
      </button>
    </form>
  );
};

export default EnquiryForm;
